import {DataStatus} from 'platform/components';
import {Show} from 'platform/foundation';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  useGetBusinessCaseFinancialProductsQuery,
  type FinancialProductsResponseBody,
} from '@dms/api/sales';

import {suffixTestId, type TestIdProps} from 'shared/web';

import {BUSINESS_CASE_FINANCIAL_PRODUCTS_SUBSCRIPTION_OPTIONS} from './BUSINESS_CASE_FINANCIAL_PRODUCTS_SUBSCRIPTION_OPTIONS';
import {FinancialProductPreferenceDialog} from './FinancialProductPreferenceDialog';
import {FinancialProductPreferenceSummary} from './FinancialProductPreferenceSummary';
import {getFinancialProductPreferenceViewModel} from './getFinancialProductPreferenceViewModel';

interface BusinessCaseFinancialProductPreferenceProps extends TestIdProps {
  businessCaseId: string;
  canUpdate: boolean;
  vehiclePrice: {amount: number; currency: string} | null;
}

type OpenedCalculator = {
  products: FinancialProductsResponseBody;
  vehiclePrice: {amount: number; currency: string};
};

export function BusinessCaseFinancialProductPreference(
  props: BusinessCaseFinancialProductPreferenceProps
) {
  const {
    data: financialProducts,
    isLoading,
    isError,
    refetch,
  } = useGetBusinessCaseFinancialProductsQuery(
    {businessCaseId: props.businessCaseId},
    BUSINESS_CASE_FINANCIAL_PRODUCTS_SUBSCRIPTION_OPTIONS
  );
  const [calculator, setCalculator] = useState<OpenedCalculator | null>(null);
  const canOpenCalculator =
    props.canUpdate &&
    isNotNil(financialProducts) &&
    financialProducts.mode !== 'CONTRACT_SNAPSHOT' &&
    isNotNil(props.vehiclePrice);
  const openCalculator = () => {
    if (canOpenCalculator && isNotNil(financialProducts) && isNotNil(props.vehiclePrice)) {
      setCalculator({products: financialProducts, vehiclePrice: props.vehiclePrice});
    }
  };
  const closeCalculator = () => {
    setCalculator(null);
    refetch();
  };

  return (
    <>
      <DataStatus isLoading={isLoading} isError={isError} minHeight={30}>
        <FinancialProductPreferenceSummary
          viewModel={getFinancialProductPreferenceViewModel(financialProducts)}
          preferenceUpdatedAt={financialProducts?.preferenceUpdatedAt}
          onOpenCalculator={canOpenCalculator ? openCalculator : undefined}
          data-testid={suffixTestId('financialProducts', props)}
        />
      </DataStatus>
      <Show when={isNotNil(calculator)}>
        <OpenedFinancialProductPreferenceDialog
          key={props.businessCaseId}
          calculator={calculator}
          products={financialProducts}
          canUpdate={canOpenCalculator}
          businessCaseId={props.businessCaseId}
          onClose={closeCalculator}
        />
      </Show>
    </>
  );
}

function OpenedFinancialProductPreferenceDialog(props: {
  calculator: OpenedCalculator | null;
  products: FinancialProductsResponseBody | undefined;
  canUpdate: boolean;
  businessCaseId: string;
  onClose: () => void;
}) {
  if (isNil(props.calculator)) {
    return null;
  }
  return (
    <FinancialProductPreferenceDialog
      businessCaseId={props.businessCaseId}
      products={props.products ?? props.calculator.products}
      vehiclePrice={props.calculator.vehiclePrice}
      canUpdate={props.canUpdate}
      onClose={props.onClose}
    />
  );
}
